import AsyncStorage from '@react-native-async-storage/async-storage';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ArticleKey } from '@/screens/auth/welcome-screen-articles';

const SETTINGS_KEY = 'demoSettings';

export interface DemoSettings {
  accentColor?: string;
  accentTextColor?: string;
  landingPageArticles?: ArticleKey[];
  landingPageLogo?: string;
  landingPageTagline?: string;
  welcomeLogoUrl?: string;
}

export interface DemoSettingsContextType {
  demoSettings: DemoSettings | null;
  setDemoSettings: React.Dispatch<React.SetStateAction<DemoSettings | null>>;
}

export const DemoSettingsContext =
  createContext<DemoSettingsContextType | null>(null);

interface Props {
  children?: React.ReactNode;
}

export const DemoSettingsContextProvider: React.FC<Props> = ({ children }) => {
  const [demoSettings, setDemoSettings] = useState<DemoSettings | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const settingsJSON = await AsyncStorage.getItem(SETTINGS_KEY);
        if (settingsJSON) {
          setDemoSettings(JSON.parse(settingsJSON) as DemoSettings);
        }
      } catch (e) {
        // Do nothing
      }

      return null;
    })();
  }, []);

  useEffect(() => {
    (async () => {
      // Update AsyncStorage whenever demo settings change
      if (demoSettings) {
        await AsyncStorage.setItem(SETTINGS_KEY, JSON.stringify(demoSettings));
      } else {
        await AsyncStorage.removeItem(SETTINGS_KEY);
      }
    })();
  }, [demoSettings]);

  const value = useMemo<DemoSettingsContextType>(() => {
    return {
      demoSettings,
      setDemoSettings,
    };
  }, [demoSettings]);

  return (
    <DemoSettingsContext.Provider value={value}>
      {children}
    </DemoSettingsContext.Provider>
  );
};

export const useDemoSettingsContext = () => {
  const context = useContext(DemoSettingsContext);
  if (!context) {
    throw new Error(
      'useDemoSettingsContext requires a DemoSettingsContextProvider',
    );
  }

  return context;
};
